import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  cancelFreeInvoice,
  createFiscalShareLink,
  createFreeInvoice,
  createQuote,
  getCustomers,
  getFiscalDocuments,
  getFiscalDocumentXml,
  getFiscalXmlArchive,
  getFreeInvoices,
  getQuotes,
  getSites,
  sendFiscalDocumentEmail,
  updateQuote,
} from "../api";
import VestaSelect from "../components/common/VestaSelect";
import ManagementFilterHeader, { formatManagementSearchInput, matchesManagementSearch, ManagementListLoading, makeManagementFilterOptions, matchesManagementFilter, sortManagementRows } from "../components/management/ManagementFilterHeader";
import useIncrementalRows from "../hooks/useIncrementalRows";
import { getUserPageLevel, readStoredUser } from "../utils/permissions";
import { buildStoredZip } from "../utils/zipArchive";
import { UtilityActionIconButton, UtilityExportOverlay, UtilityFullscreenPreview, printUtilityPreview } from "./UtilityWindows";
import { createFiscalVisualExportBlob, printFiscalPreviewRoot } from "../utils/fiscalDocumentExport";
import vestaLogo from "../assets/images/svg/logo_img.png";
import "../assets/css/windows.css";
import "../assets/css/overlay.css";
import "../assets/css/bookings.css";
import "../assets/css/management.css";
import "../assets/css/bookingAccounts.css";
import "../assets/css/documents.css";

const EMPTY_ROW = { description: "", quantity: 1, unitPrice: 0, discountRate: 0, vatRate: 22 };
const DOCUMENT_LABELS = { INVOICE: "Fattura", RECEIPT: "Ricevuta", CREDIT_NOTE: "Nota di Credito", RECEIPT_REVERSAL: "Storno Ricevuta" };

const QUOTE_FILTER_DEFINITIONS = [
  { key: "quoteNumber", label: "Numero", getter: (item) => item.quoteNumber || "-", type: "text" },
  { key: "customerName", label: "Cliente", getter: (item) => item.customerName || "-", type: "text" },
  { key: "issuedAt", label: "Data", getter: (item) => dateLabel(item.issuedAt), filterGetter: (item) => item.issuedAt, type: "date" },
  { key: "validUntil", label: "Validità", getter: (item) => dateLabel(item.validUntil), filterGetter: (item) => item.validUntil, type: "date" },
  { key: "status", label: "Stato", getter: (item) => statusLabel(item.status) },
  { key: "grossTotal", label: "Totale", getter: (item) => money(item.grossTotal), filterGetter: (item) => item.grossTotal, type: "number" },
];

const FREE_INVOICE_FILTER_DEFINITIONS = [
  { key: "documentNumber", label: "Numero", getter: (item) => item.documentNumber || "-", type: "text" },
  { key: "customerName", label: "Cliente", getter: (item) => item.customerName || "-", type: "text" },
  { key: "issuedAt", label: "Data", getter: (item) => dateLabel(item.issuedAt), filterGetter: (item) => item.issuedAt, type: "date" },
  { key: "siteDescription", label: "Sede", getter: (item) => item.siteDescription || "Sede non indicata" },
  { key: "status", label: "Stato", getter: (item) => statusLabel(item.status) },
  { key: "grossTotal", label: "Totale", getter: (item) => money(item.grossTotal), filterGetter: (item) => item.grossTotal, type: "number" },
];

const INVOICING_FILTER_DEFINITIONS = [
  { key: "documentNumber", label: "Numero", getter: (item) => item.documentNumber || "-", type: "text" },
  { key: "customerName", label: "Cliente", getter: (item) => item.customerName || "-", type: "text" },
  { key: "issuedAt", label: "Data", getter: (item) => dateLabel(item.issuedAt), filterGetter: (item) => item.issuedAt, type: "date" },
  { key: "siteDescription", label: "Sede", getter: (item) => item.siteDescription || "Sede non indicata" },
  { key: "roomNumber", label: "Camera", getter: (item) => item.roomNumber || "-" },
  { key: "grossTotal", label: "Totale", getter: (item) => money(item.grossTotal), filterGetter: (item) => item.grossTotal, type: "number" },
];

function todayToken() {
  return new Date().toISOString().slice(0, 10);
}

function dateToken(value) {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10);
}

function storedDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const serialized = String(value || "").trim();
  const useStoredClock = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(serialized);
  const year = useStoredClock ? date.getUTCFullYear() : date.getFullYear();
  const month = String((useStoredClock ? date.getUTCMonth() : date.getMonth()) + 1).padStart(2, "0");
  const day = String(useStoredClock ? date.getUTCDate() : date.getDate()).padStart(2, "0");
  const hours = String(useStoredClock ? date.getUTCHours() : date.getHours()).padStart(2, "0");
  const minutes = String(useStoredClock ? date.getUTCMinutes() : date.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function dateLabel(value) {
  const normalized = storedDateTime(value);
  if (!normalized) return "-";
  const [year, month, day] = normalized.slice(0, 10).split("-");
  return `${day}/${month}/${year}`;
}

function timeLabel(value) {
  return storedDateTime(value)?.slice(11, 16) || "-";
}

function statusLabel(status) {
  const token = String(status || "").toUpperCase();
  return token === "ISSUED" ? "Emesso" : token === "CANCELLED" ? "Annullato" : token === "CLOSED" ? "Chiuso" : "Bozza";
}

function money(value) {
  return `${Number(value || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}

function rowAmounts(row) {
  const quantity = Math.max(0, Number(row.quantity || 0));
  const unitPrice = Math.max(0, Number(row.unitPrice || 0));
  const discountRate = Math.min(100, Math.max(0, Number(row.discountRate || 0)));
  const vatRate = Math.min(100, Math.max(0, Number(row.vatRate || 0)));
  const taxable = quantity * unitPrice * (1 - discountRate / 100);
  const tax = taxable * vatRate / 100;
  return { taxable, tax, total: taxable + tax };
}

function totals(rows) {
  return (rows || []).reduce((acc, row) => {
    const amount = rowAmounts(row);
    acc.taxable += amount.taxable;
    acc.tax += amount.tax;
    acc.total += amount.total;
    return acc;
  }, { taxable: 0, tax: 0, total: 0 });
}

function customerDisplayName(customer = {}) {
  return customer.businessName || customer.ragioneSociale || [customer.surname || customer.cognome, customer.name || customer.nome].filter(Boolean).join(" ") || "Cliente";
}

function customerAddress(customer = {}) {
  return [customer.address, customer.streetNumber, customer.postalCode, customer.city, customer.province].filter(Boolean).join(" ");
}

function customerContact(customer = {}, typeTokens = []) {
  const contacts = Array.isArray(customer.contacts) ? customer.contacts : [];
  const item = contacts.find((contact) => typeTokens.some((token) => String(contact.contactType || "").toLowerCase().includes(token)));
  return String(item?.contactValue || "").trim();
}

function normalizePhone(phone) {
  let digits = String(phone || "").replace(/\D/g, "");
  if (digits.startsWith("00")) digits = digits.slice(2);
  if (digits.startsWith("0")) digits = `39${digits}`;
  if (/^3\d{9}$/.test(digits)) digits = `39${digits}`;
  return digits;
}

function customerContactRecord(customer = {}, types = []) {
  const wanted = types.map((value) => String(value || "").trim().toLocaleLowerCase("it")).filter(Boolean);
  const contacts = Array.isArray(customer?.contacts) ? customer.contacts : [];
  const matches = contacts.filter((contact) => wanted.includes(String(contact?.contactType || contact?.tipoContatto || "").trim().toLocaleLowerCase("it")) && String(contact?.contactValue || contact?.datoContatto || "").trim());
  return matches.find((contact) => contact?.primary === true || contact?.principale === true) || matches[0] || null;
}

function fiscalRegistryCustomer(item = {}, customers = []) {
  const customerId = String(item?.invoiceCustomerId || item?.customerId || "");
  if (!customerId) return null;
  return (customers || []).find((customer) => String(customer?._id || "") === customerId) || null;
}

function registryEmail(customer = {}) {
  const contact = customerContactRecord(customer, ["Email"]) || customerContactRecord(customer, ["PEC"]);
  return String(contact?.contactValue || contact?.datoContatto || "").trim();
}

function registryMobile(customer = {}) {
  const contact = customerContactRecord(customer, ["Cellulare"]);
  if (!contact) return "";
  const prefix = String(contact?.countryPrefixValue || "").replace(/\D/g, "");
  const number = String(contact?.contactValue || contact?.datoContatto || "").replace(/\D/g, "");
  return normalizePhone(prefix ? `${prefix}${number}` : number);
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function responseError(error, fallback) {
  return error?.response?.data?.error || error?.message || fallback;
}

function emptyQuote() {
  const issuedAt = todayToken();
  const valid = new Date();
  valid.setDate(valid.getDate() + 15);
  return { customerId: "", siteId: "", customerName: "", customerEmail: "", customerPhone: "", customerAddress: "", issuedAt, validUntil: valid.toISOString().slice(0, 10), status: "DRAFT", rows: [{ ...EMPTY_ROW }], note: "" };
}

function emptyInvoice() {
  return { customerId: "", siteId: "", customerName: "", customerEmail: "", customerPhone: "", customerAddress: "", issuedAt: todayToken(), numberingMode: "AUTO", documentNumber: "", orientation: "PORTRAIT", rows: [{ ...EMPTY_ROW }], note: "" };
}

function useReferenceData() {
  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  useEffect(() => {
    let active = true;
    getCustomers().then((data) => { if (active) setCustomers(data?.items || []); }).catch(() => {});
    getSites().then((data) => { if (active) setSites(data?.items || []); }).catch(() => {});
    return () => { active = false; };
  }, []);
  return { customers, sites };
}

function useManagementList(items, definitions, searchGetter) {
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("");
  const [columnFilters, setColumnFilters] = useState(() => Object.fromEntries(definitions.map(({ key }) => [key, []])));
  const [sortState, setSortState] = useState(() => ({ key: definitions[0]?.key || "", direction: "asc" }));

  const filterOptions = useMemo(() => Object.fromEntries(definitions.map((definition) => [
    definition.key,
    definition.type === "date" || definition.type === "time" || definition.type === "number"
      ? []
      : makeManagementFilterOptions(items, definition.getter),
  ])), [items, definitions]);

  const filtered = useMemo(() => {
    return items.filter((item) => {
      if (!matchesManagementSearch(searchGetter(item), search)) return false;
      return definitions.every((definition) => matchesManagementFilter(
        (definition.filterGetter || definition.getter)(item),
        columnFilters[definition.key],
        definition.type || "checks"
      ));
    });
  }, [items, definitions, search, searchGetter, columnFilters]);

  const sortGetters = useMemo(() => Object.fromEntries(definitions.map((definition) => [definition.key, {
    getter: definition.filterGetter || definition.getter,
    type: definition.type || "text",
  }])), [definitions]);
  const ordered = useMemo(() => sortManagementRows(filtered, sortState, sortGetters), [filtered, sortState, sortGetters]);
  const incrementalRows = useIncrementalRows(ordered);

  function toggleFilter(columnKey) {
    setActiveFilter((current) => current === columnKey ? "" : columnKey);
  }

  function changeFilter(columnKey, value, checked) {
    setColumnFilters((current) => {
      const values = Array.isArray(current[columnKey]) ? current[columnKey] : [];
      const nextValues = checked ? [...new Set([...values, value])] : values.filter((item) => item !== value);
      return { ...current, [columnKey]: nextValues };
    });
  }

  function changeRangeFilter(columnKey, value) {
    setColumnFilters((current) => ({ ...current, [columnKey]: value }));
  }

  function clearFilter(columnKey) {
    setColumnFilters((current) => ({ ...current, [columnKey]: [] }));
  }

  function toggleSort(columnKey) {
    setSortState((current) => ({ key: columnKey, direction: current.key === columnKey && current.direction === "asc" ? "desc" : "asc" }));
  }

  function reset() {
    setSearch("");
    setActiveFilter("");
    setColumnFilters(Object.fromEntries(definitions.map(({ key }) => [key, []])));
    setSortState({ key: definitions[0]?.key || "", direction: "asc" });
  }

  return {
    search,
    setSearch,
    activeFilter,
    columnFilters,
    filterOptions,
    filtered: ordered,
    rows: incrementalRows.rows,
    shellRef: incrementalRows.shellRef,
    handleScroll: incrementalRows.handleScroll,
    sortState,
    reset,
    filterProps: { activeFilter, onToggle: toggleFilter, onCheckChange: changeFilter, onRangeChange: changeRangeFilter, onClear: clearFilter, onSort: toggleSort },
  };
}

function DocumentBanner({ title, subtitle }) {
  return <div className="window_banner"><div className="window_banner_copy"><h1>{title}</h1><p>{subtitle}</p></div></div>;
}

function DocumentOptionsMenu({ actions = [] }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  useEffect(() => {
    function close(event) {
      if (!menuRef.current?.contains(event.target)) setOpen(false);
    }
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  return <div className="management_options_menu" ref={menuRef}>
    <button type="button" className="management_options_button" onClick={() => setOpen((current) => !current)} aria-label="Opzioni" aria-expanded={open}><span></span><span></span><span></span></button>
    {open && <div className="management_options_dropdown">{actions.filter((action) => !action.hidden).map((action) => <button key={action.label} type="button" className={`management_options_item${action.danger ? " is_danger" : ""}`} disabled={!!action.disabled} onClick={() => { if (action.disabled) return; setOpen(false); action.onClick?.(); }}>{action.label}</button>)}</div>}
  </div>;
}

function DocumentToolbar({ actions, search, onSearchChange, results, children, filtersExpanded = false, onToggleFilters = null }) {
  const expandableFilters = typeof onToggleFilters === "function";
  return <div className={`booking_toolbar management_toolbar management_table_toolbar management_options_search_toolbar management_shared_toolbar documents_management_toolbar${expandableFilters ? ` documents_filters_toolbar${filtersExpanded ? " is_filters_expanded" : ""}` : ""}`}>
    <DocumentOptionsMenu actions={actions} />
    <input className="management_search" value={search} onChange={(event) => onSearchChange(formatManagementSearchInput(event.target.value))} placeholder="Cerca documenti (es. Mario, Rossi)" />
    {expandableFilters && (
      <button
        type="button"
        className="vesta_filters_toggle documents_filters_toggle"
        onClick={onToggleFilters}
        aria-expanded={filtersExpanded}
        aria-label={filtersExpanded ? "Accorcia filtri fatturazione" : "Espandi filtri fatturazione"}
        title={filtersExpanded ? "Accorcia filtri" : "Espandi filtri"}
      >
        <span className="vesta_filters_toggle_icon" aria-hidden="true"><span></span><span></span></span>
      </button>
    )}
    <div className="management_toolbar_results"><strong className="management_results_count">Risultati: {results}</strong></div>
    {children}
  </div>;
}

function StatusBadge({ status }) {
  const token = String(status || "").toUpperCase();
  return <span className={`documents_status is_${token.toLowerCase()}`}>{statusLabel(status)}</span>;
}

function FilterCell({ definition, list }) {
  return <ManagementFilterHeader
    columnKey={definition.key}
    label={definition.label}
    options={list.filterOptions[definition.key] || []}
    value={list.columnFilters[definition.key] || []}
    filterType={definition.type || "checks"}
    sortDirection={list.sortState.key === definition.key ? list.sortState.direction : ""}
    {...list.filterProps}
  />;
}

function RowsEditor({ rows, onChange, defaultVatRate = 22 }) {
  function patch(index, key, value) {
    const next = rows.map((row, rowIndex) => rowIndex === index ? { ...row, [key]: value } : row);
    onChange(next);
  }
  function add() { onChange([...rows, { ...EMPTY_ROW, vatRate: defaultVatRate }]); }
  function remove(index) { if (rows.length > 1) onChange(rows.filter((_, rowIndex) => rowIndex !== index)); }
  const sum = totals(rows);
  return <div className="documents_rows_wrap">
    <div className="documents_rows_header"><h3>Righe Documento</h3><button type="button" className="documents_secondary_button" onClick={add}>+ Aggiungi Riga</button></div>
    <div className="documents_rows_table">
      <div className="documents_rows_titles"><span>Descrizione</span><span>Q.tà</span><span>Prezzo</span><span>% Sconto</span><span>% IVA</span><span>Totale</span><span></span></div>
      {rows.map((row, index) => <div className="documents_row" key={`row_${index}`}>
        <input value={row.description || ""} onChange={(event) => patch(index, "description", event.target.value)} />
        <input type="number" min="0" step="0.01" value={row.quantity ?? 1} onChange={(event) => patch(index, "quantity", event.target.value)} />
        <input type="number" min="0" step="0.01" value={row.unitPrice ?? 0} onChange={(event) => patch(index, "unitPrice", event.target.value)} />
        <input type="number" min="0" max="100" step="0.01" value={row.discountRate ?? 0} onChange={(event) => patch(index, "discountRate", event.target.value)} />
        <input type="number" min="0" max="100" step="0.01" value={row.vatRate ?? 0} onChange={(event) => patch(index, "vatRate", event.target.value)} />
        <output>{money(rowAmounts(row).total)}</output>
        <button type="button" className="documents_row_remove" disabled={rows.length <= 1} onClick={() => remove(index)} aria-label="Elimina riga">×</button>
      </div>)}
    </div>
    <div className="documents_totals"><div><span>Imponibile</span><strong>{money(sum.taxable)}</strong></div><div><span>Imposta</span><strong>{money(sum.tax)}</strong></div><div className="is_total"><span>Totale</span><strong>{money(sum.total)}</strong></div></div>
  </div>;
}

function siteDefaultVatRate(site = {}) {
  const value = Number(site?.defaults?.booking?.defaultVatRate?.value ?? 22);
  return Number.isFinite(value) ? Math.min(100, Math.max(0, value)) : 22;
}

function CustomerFields({ draft, customers, sites, onChange }) {
  function selectCustomer(id) {
    const customer = customers.find((item) => String(item._id) === String(id));
    onChange({
      customerId: id,
      customerName: customer ? customerDisplayName(customer) : "",
      customerEmail: customer ? customerContact(customer, ["mail", "pec"]) : "",
      customerPhone: customer ? customerContact(customer, ["cell", "tel"]) : "",
      customerAddress: customer ? customerAddress(customer) : "",
    });
  }
  return <div className="documents_form_grid">
    <label><span>Sede</span><VestaSelect value={draft.siteId || ""} onChange={(event) => { const siteId = event.target.value; const selectedSite = sites.find((site) => String(site._id) === String(siteId)); const vatRate = siteDefaultVatRate(selectedSite); onChange({ siteId, rows: (draft.rows || []).map((row) => Number(row.vatRate || 0) > 0 ? row : { ...row, vatRate }) }); }}><option value="">Seleziona sede</option>{sites.map((site) => <option key={site._id} value={site._id}>{site.description}</option>)}</VestaSelect></label>
    <label><span>Cliente esistente</span><VestaSelect value={draft.customerId || ""} onChange={(event) => selectCustomer(event.target.value)}><option value="">Inserimento libero</option>{customers.map((customer) => <option key={customer._id} value={customer._id}>{customerDisplayName(customer)}</option>)}</VestaSelect></label>
    <label><span>Intestatario *</span><input value={draft.customerName || ""} onChange={(event) => onChange({ customerName: event.target.value })} /></label>
    <label><span>Indirizzo</span><input value={draft.customerAddress || ""} onChange={(event) => onChange({ customerAddress: event.target.value })} /></label>
    <label><span>Email</span><input type="email" value={draft.customerEmail || ""} onChange={(event) => onChange({ customerEmail: event.target.value })} /></label>
    <label><span>Telefono / Cellulare</span><input value={draft.customerPhone || ""} onChange={(event) => onChange({ customerPhone: event.target.value })} /></label>
  </div>;
}

function EditorOverlay({ kind, draft, customers, sites, error, onChange, onClose, onSave }) {
  const invoice = kind === "invoice";
  return <div className="overlay_backdrop"><div className="overlay_card documents_editor" role="dialog" aria-modal="true">
    <div className="overlay_header"><h2>{invoice ? "NUOVA FATTURA LIBERA" : draft._id ? "MODIFICA PREVENTIVO" : "NUOVO PREVENTIVO"}</h2><button type="button" className="overlay_close_button" onClick={onClose}>×</button></div>
    <div className="overlay_body">
      <section className="documents_editor_section"><h3>Dati Documento</h3><CustomerFields draft={draft} customers={customers} sites={sites} onChange={onChange} />
        <div className="documents_form_grid">
          <label><span>Data Documento *</span><input type="date" value={draft.issuedAt || ""} onChange={(event) => onChange({ issuedAt: event.target.value })} /></label>
          {!invoice && <label><span>Valido fino al</span><input type="date" value={draft.validUntil || ""} onChange={(event) => onChange({ validUntil: event.target.value })} /></label>}
          {!invoice && <label><span>Stato</span><VestaSelect value={draft.status || "DRAFT"} onChange={(event) => onChange({ status: event.target.value })}><option value="DRAFT">Bozza</option><option value="CLOSED">Chiuso</option></VestaSelect></label>}
          {invoice && <label className="documents_checkbox_field"><span>Numerazione</span><label className="documents_checkbox"><input type="checkbox" checked={draft.numberingMode === "MANUAL"} onChange={(event) => onChange({ numberingMode: event.target.checked ? "MANUAL" : "AUTO", documentNumber: event.target.checked ? draft.documentNumber : "" })} /><span>Numerazione manuale</span></label></label>}
          {invoice && draft.numberingMode === "MANUAL" && <label><span>Numero Documento *</span><input inputMode="numeric" value={draft.documentNumber || ""} onChange={(event) => onChange({ documentNumber: event.target.value.replace(/\D/g, "") })} /></label>}
          {invoice && <label><span>Orientamento</span><VestaSelect value={draft.orientation || "PORTRAIT"} onChange={(event) => onChange({ orientation: event.target.value })}><option value="PORTRAIT">Verticale</option><option value="LANDSCAPE">Orizzontale</option></VestaSelect></label>}
        </div>
      </section>
      <section className="documents_editor_section"><RowsEditor rows={draft.rows || []} defaultVatRate={siteDefaultVatRate(sites.find((site) => String(site._id) === String(draft.siteId)))} onChange={(rows) => onChange({ rows })} /></section>
      <section className="documents_editor_section"><h3>Note</h3><textarea className="documents_notes" value={draft.note || ""} onChange={(event) => onChange({ note: event.target.value })} /></section>
    </div>
    <div className="overlay_footer"><div className="overlay_error_message">{error}</div><button type="button" className="overlay_save_button overlay_save_close_button" onClick={onSave}>{invoice ? "Emetti Fattura" : "Salva e Stampa"}</button></div>
  </div></div>;
}

function QuotePrintSheet({ quote }) {
  if (!quote) return null;
  return <article className="document_print_sheet">
    <header><div><h1>Preventivo</h1><strong>{quote.quoteNumber}</strong></div><div><span>Data</span><strong>{dateLabel(quote.issuedAt)}</strong><span>Validità</span><strong>{dateLabel(quote.validUntil)}</strong></div></header>
    <section><h2>Cliente</h2><strong>{quote.customerName}</strong><p>{quote.customerAddress || ""}</p><p>{quote.customerEmail || ""} {quote.customerPhone || ""}</p></section>
    <table><thead><tr><th>Descrizione</th><th>Q.tà</th><th>Prezzo</th><th>Sconto</th><th>IVA</th><th>Totale</th></tr></thead><tbody>{(quote.rows || []).map((row, index) => <tr key={index}><td>{row.description}</td><td>{row.quantity}</td><td>{money(row.unitPrice)}</td><td>{row.discountRate || 0}%</td><td>{row.vatRate || 0}%</td><td>{money(row.grossTotal ?? rowAmounts(row).total)}</td></tr>)}</tbody></table>
    <div className="document_print_totals"><span>Imponibile <strong>{money(quote.taxableTotal)}</strong></span><span>Imposta <strong>{money(quote.taxAmount)}</strong></span><span>Totale <strong>{money(quote.grossTotal)}</strong></span></div>
    {quote.note && <section><h2>Note</h2><p>{quote.note}</p></section>}
  </article>;
}

function TableState({ loading, empty, loadingText, emptyText }) {
  if (loading) return <ManagementListLoading label={loadingText} />;
  if (empty) return <div className="management_table_visible_status">{emptyText}</div>;
  return null;
}

export function Quotes() {
  const { customers, sites } = useReferenceData();
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [printQuote, setPrintQuote] = useState(null);
  const level = getUserPageLevel(readStoredUser(), "quote");
  const list = useManagementList(items, QUOTE_FILTER_DEFINITIONS, (item) => [item.quoteNumber, item.customerName, item.customerEmail, item.note].join(" "));

  async function reload() {
    setLoading(true);
    try { const data = await getQuotes(); setItems(data?.items || []); setError(""); } catch (err) { setError(responseError(err, "Errore caricamento preventivi")); } finally { setLoading(false); }
  }
  useEffect(() => { reload(); }, []);

  function edit(item) { setDraft({ ...item, issuedAt: dateToken(item.issuedAt), validUntil: dateToken(item.validUntil), rows: (item.rows || []).map((row) => ({ ...row })) }); setError(""); }
  function print(item) { setPrintQuote(item); setTimeout(() => window.print(), 50); }
  async function save() {
    if (!String(draft?.customerName || "").trim()) return setError("Inserisci l'intestatario del preventivo");
    if (!(draft?.rows || []).some((row) => String(row.description || "").trim())) return setError("Inserisci almeno una riga nel preventivo");
    try {
      const result = draft._id ? await updateQuote(draft._id, draft) : await createQuote(draft);
      const savedQuote = result?.item || draft;
      setDraft(null);
      setError("");
      setPrintQuote(savedQuote);
      await reload();
      setTimeout(() => window.print(), 50);
    } catch (err) { setError(responseError(err, "Errore salvataggio preventivo")); }
  }
  function newQuote() { setDraft(emptyQuote()); setError(""); }

  return <section className="window_page bookings_page management_page management_shared_page documents_management_page">
    <DocumentBanner title="PREVENTIVI" subtitle="Crea, modifica e stampa preventivi con righe, sconti, IVA e validità." />
    <div className="management_list_panel documents_management_panel">
      <DocumentToolbar actions={[{ label: "Nuovo Preventivo", onClick: newQuote }, { label: "Azzera ricerca e filtri", onClick: list.reset }, { label: "Aggiorna", onClick: reload }]} search={list.search} onSearchChange={list.setSearch} results={list.filtered.length} />
      {error && !draft && <div className="management_table_visible_status is_error">{error}</div>}
      <div className="management_board management_table_board"><div className="management_table_shell" role="table" aria-label="Elenco preventivi" ref={list.shellRef} onScroll={list.handleScroll}>
        <div className="management_table_header management_shared_table_header documents_quotes_table_header" role="row">
          {QUOTE_FILTER_DEFINITIONS.map((definition) => <FilterCell key={definition.key} definition={definition} list={list} />)}
          <div className="management_table_actions_head" role="columnheader">Azioni</div>
        </div>
        <TableState loading={loading} empty={!loading && list.filtered.length === 0} loadingText="Caricamento Preventivi" emptyText="Nessun Risultato Trovato" />
        <div className="management_table_body">{list.rows.map((item) => <article className="management_table_row documents_quotes_table_row" key={item._id} data-vesta-focus-id={item._id} role="row">
          <div className="management_table_cell management_table_title_cell" role="cell"><strong>{item.quoteNumber || "-"}</strong></div>
          <div className="management_table_cell" role="cell"><div className="documents_customer_cell"><strong>{item.customerName || "-"}</strong><span>{item.customerEmail || ""}</span></div></div>
          <div className="management_table_cell" role="cell">{dateLabel(item.issuedAt)}</div>
          <div className="management_table_cell" role="cell">{dateLabel(item.validUntil)}</div>
          <div className="management_table_cell" role="cell"><StatusBadge status={item.status} /></div>
          <div className="management_table_cell documents_money_cell" role="cell"><strong>{money(item.grossTotal)}</strong></div>
          <div className="management_table_actions management_status_actions" role="cell">{level >= 2 && <button type="button" className="management_action_button management_action_primary" onClick={() => edit(item)}>Modifica</button>}<button type="button" className="management_action_button" onClick={() => print(item)}>Stampa</button></div>
        </article>)}</div>
      </div></div>
    </div>
    {draft && <EditorOverlay kind="quote" draft={draft} customers={customers} sites={sites} error={error} onChange={(patch) => setDraft((current) => ({ ...current, ...patch }))} onClose={() => setDraft(null)} onSave={save} />}
    <QuotePrintSheet quote={printQuote} />
  </section>;
}


function fiscalPercent(value) {
  return Number(value || 0).toLocaleString("it-IT", { maximumFractionDigits: 2 });
}

function fiscalPreviewRowCells(row = {}) {
  const quantity = Math.max(0, Number(row.quantity || 0));
  const unitPrice = Math.max(0, Number(row.unitPrice || 0));
  const taxable = Number(row.taxableTotal ?? (quantity * unitPrice) ?? 0);
  const net = Number(row.netTotal ?? row.taxableTotal ?? 0);
  const vatRate = Number(row.vatRate);
  const tax = Number(row.taxAmount ?? (Number.isFinite(vatRate) ? net * vatRate / 100 : 0));
  const gross = Number(row.grossTotal ?? row.total ?? (net + tax));
  return [
    row.description || "-",
    money(unitPrice),
    String(quantity),
    money(taxable),
    fiscalPercent(row.discountRate),
    money(net),
    Number.isFinite(vatRate) ? fiscalPercent(vatRate) : "-",
    money(tax),
    money(gross),
  ];
}

function fiscalVatSummary(item = {}) {
  const rows = Array.isArray(item.rows) ? item.rows : [];
  const baseNet = rows.reduce((sum, row) => sum + Number(row.netTotal ?? row.taxableTotal ?? 0), 0);
  const factor = baseNet > 0 ? Math.max(0, Number(item.taxableTotal || 0)) / baseNet : 1;
  const grouped = new Map();
  rows.forEach((row) => {
    const rate = Number(row.vatRate);
    if (!Number.isFinite(rate) || rate < 0) return;
    const taxable = Number(row.netTotal ?? row.taxableTotal ?? 0) * factor;
    const tax = taxable * rate / 100;
    const current = grouped.get(rate) || { rate, taxable: 0, tax: 0, total: 0 };
    current.taxable += taxable;
    current.tax += tax;
    current.total += taxable + tax;
    grouped.set(rate, current);
  });
  return [...grouped.values()].sort((a, b) => a.rate - b.rate);
}

function fiscalPreviewPages(item = {}, orientation = "portrait") {
  const rows = (Array.isArray(item.rows) ? item.rows : []).map(fiscalPreviewRowCells);
  const pageSize = orientation === "landscape" ? 5 : 8;
  const pages = [];
  for (let index = 0; index < rows.length; index += pageSize) pages.push(rows.slice(index, index + pageSize));
  return pages.length ? pages : [[]];
}

function fiscalNights(item = {}) {
  const startValue = storedDateTime(item.checkIn);
  const endValue = storedDateTime(item.checkOut);
  if (!startValue || !endValue || new Date(endValue) <= new Date(startValue)) return "-";
  const startDay = Date.parse(`${startValue.slice(0, 10)}T00:00:00Z`);
  const endDay = Date.parse(`${endValue.slice(0, 10)}T00:00:00Z`);
  return String(Math.max(1, Math.round((endDay - startDay) / 86400000)));
}

function FiscalDocumentPreviewSheet({ item, orientation, pageIndex, pageRows, pageCount, printMode = false }) {
  const company = item?.companyProfile || {};
  const legalSite = item?.legalSite || {};
  const documentLabel = DOCUMENT_LABELS[item?.documentType] || item?.documentType || "Documento";
  const isInvoice = item?.documentType === "INVOICE" || item?.documentType === "CREDIT_NOTE";
  const companyLogo = company.logoUrl || vestaLogo;
  const legalAddress = [legalSite.address, legalSite.streetNumber].filter(Boolean).join(" ") || "-";
  const legalLocation = [[legalSite.cap, legalSite.city].filter(Boolean).join(" "), legalSite.province ? `(${legalSite.province})` : ""].filter(Boolean).join(" ") || "-";
  const vatSummary = fiscalVatSummary(item);
  const roundingAmount = Number(item?.roundingAmount || 0);
  const roundingLabel = roundingAmount > 0.000001 ? "Abbuono Attivo" : roundingAmount < -0.000001 ? "Abbuono Passivo" : "Abbuono";
  const lastPage = pageIndex === pageCount - 1;
  const customerFields = [
    ["Intestatario Fattura", item?.customerName || "-", "is_wide is_primary_value"],
    ["Indirizzo", item?.customerAddress || "-", "is_wide"],
    ["Codice Fiscale", item?.customerFiscalCode || "-", ""],
    ["Partita IVA", item?.customerVatNumber || "-", ""],
    ["Codice SDI", item?.customerSdiCode || "-", ""],
    ["Telefono", item?.customerPhone || "-", ""],
    ["Mail / PEC", item?.customerEmail || "-", "is_wide"],
  ];
  const stayFields = [
    ["Ospite", item?.customerName || "-", "is_wide is_primary_value"],
    ["Camera", item?.roomNumber || "-", ""],
    ["Check In", dateLabel(item?.checkIn), ""],
    ["Ora Check In", timeLabel(item?.checkIn), ""],
    ["Check Out", dateLabel(item?.checkOut), ""],
    ["Ora Check Out", timeLabel(item?.checkOut), ""],
    ["Notti", fiscalNights(item), ""],
    ["Origine", item?.sourceType === "FREE_INVOICE" ? "Fattura libera" : "Soggiorno", ""],
    ["Note Documento", String(item?.note || "").trim() || "-", "is_wide is_note_value"],
  ];

  return (
    <div className={`utility_page_sheet booking_fiscal_report booking_fiscal_report_v2 ${isInvoice ? "booking_invoice_report" : "booking_receipt_report"}${!printMode ? " utility_screen_sheet" : ""}${orientation === "landscape" ? " is_landscape" : ""}`}>
      <div className="booking_fiscal_report_header booking_fiscal_report_header_v2">
        <div className={`booking_fiscal_company_logo_box${company.logoUrl ? "" : " is_vesta_fallback"}`}><img src={companyLogo} alt={company.description || "VestaMNG"} /></div>
        <div className="booking_fiscal_company_identity">
          <strong>{company.description || "VestaMNG"}</strong>
          <span>P. IVA: {company.vatNumber || "-"}</span>
          <span>Codice Fiscale: {company.fiscalCode || "-"}</span>
          <span>Capitale Sociale: {Number(company.shareCapital || 0) > 0 ? money(company.shareCapital) : "-"}</span>
          <span>REA: {company.reaCode || "-"}</span>
          <span>Codice SDI: {company.sdiCode || "-"}</span>
        </div>
        <div className="booking_fiscal_legal_site">
          <span><b>Indirizzo:</b> {legalAddress}</span>
          <span><b>Località:</b> {legalLocation}</span>
          <span><b>Telefono:</b> {legalSite.phone || "-"}</span>
          <span><b>Email:</b> {legalSite.email || "-"}</span>
          <span><b>Banca:</b> {[company.primaryBank?.bankName, company.primaryBank?.branchName].filter(Boolean).join(" - ") || "-"} · <b>IBAN:</b> {company.primaryBank?.iban || company.iban || "-"}</span>
        </div>
        <div className="booking_fiscal_header_spacer" aria-hidden="true" />
        <div className="booking_fiscal_vesta_logo_box"><img src={vestaLogo} alt="Vesta" /></div>
      </div>
      <div className="booking_fiscal_report_rule" />

      <div className="booking_fiscal_info_sections">
        <section className="booking_fiscal_info_card booking_fiscal_info_card_v2">
          <h4>Dati Cliente</h4>
          <div className="booking_fiscal_field_grid">{customerFields.map(([label, value, className]) => <div key={label} className={className || undefined}><span>{label}</span><strong>{value || "-"}</strong></div>)}</div>
        </section>
        <section className="booking_fiscal_info_card booking_fiscal_info_card_v2">
          <h4>Dati Soggiorno</h4>
          <div className="booking_fiscal_field_grid">{stayFields.map(([label, value, className]) => <div key={label} className={className || undefined}><span>{label}</span><strong>{value || "-"}</strong></div>)}</div>
        </section>
      </div>

      <section className="booking_fiscal_document_data_section">
        <h4>Dati Documento</h4>
        <div className="booking_fiscal_document_data_grid">
          <div><span>Tipo Documento</span><strong>{documentLabel}</strong></div>
          <div><span>Numero / Sezionale</span><strong>{item?.documentNumber || "-"} / {item?.fiscalSection || "-"}</strong></div>
          <div><span>Data</span><strong>{dateLabel(item?.issuedAt)}</strong></div>
        </div>
      </section>

      <div className="booking_fiscal_table_wrap">
        <table className="booking_fiscal_table booking_fiscal_table_v2">
          <thead><tr><th>Descrizione</th><th>Prezzo</th><th>Qta</th><th>Imponibile</th><th>% Sconto</th><th>Netto</th><th>% IVA</th><th>Imposta</th><th>Ivato</th></tr></thead>
          <tbody>
            {pageRows.map((row, rowIndex) => <tr key={`${pageIndex}-${rowIndex}`}>{row.map((cell, cellIndex) => <td key={`${pageIndex}-${rowIndex}-${cellIndex}`}>{cell}</td>)}</tr>)}
            {!pageRows.length && <tr><td colSpan="9">Nessun addebito presente</td></tr>}
          </tbody>
        </table>
      </div>

      {lastPage && <div className={`booking_fiscal_bottom_zone booking_fiscal_bottom_zone_v2 ${isInvoice ? "booking_invoice_bottom_zone" : "booking_receipt_bottom_zone"}`}>
        {isInvoice ? <div className="booking_invoice_vat_box">
          <div className="booking_invoice_vat_title"><span>Castelletto IVA</span></div>
          <div className="booking_invoice_vat_table">
            <div className="booking_invoice_vat_row booking_invoice_vat_header"><span>% IVA</span><span>Imponibile</span><span>Imposta</span><span>Totale Ivato</span></div>
            {vatSummary.map((entry) => <div className="booking_invoice_vat_row" key={entry.rate}><strong>{fiscalPercent(entry.rate)}%</strong><strong>{money(entry.taxable)}</strong><strong>{money(entry.tax)}</strong><strong>{money(entry.total)}</strong></div>)}
            {!vatSummary.length && <div className="booking_invoice_vat_empty">Nessun importo IVA</div>}
          </div>
        </div> : <div className="booking_fiscal_bottom_spacer" aria-hidden="true" />}
        <div className="booking_fiscal_totals_panel booking_fiscal_totals_panel_v2">
          <div><span>Totale Imponibile</span><strong>{money(item?.taxableTotal)}</strong></div>
          <div><span>Totale Acconti / Caparre</span><strong>{money(item?.depositAmount)}</strong></div>
          <div><span>Sconto Incondizionato</span><strong>- {money(item?.unconditionalDiscountAmount)}</strong></div>
          <div><span>Totale Imposta</span><strong>{money(item?.taxAmount)}</strong></div>
          <div><span>{roundingLabel}</span><strong>{roundingAmount > 0 ? "+ " : roundingAmount < 0 ? "- " : ""}{money(Math.abs(roundingAmount))}</strong></div>
          <div className="booking_fiscal_document_total"><span>Totale {documentLabel}</span><strong>{money(item?.grossTotal)}</strong></div>
        </div>
      </div>}
      <div className="booking_fiscal_report_footer booking_fiscal_report_footer_v2"><strong>Pagina {pageIndex + 1} / {pageCount}</strong></div>
    </div>
  );
}

function FiscalDocumentFullscreenPreview({ item, onClose }) {
  const [orientation, setOrientation] = useState("portrait");
  const [page, setPage] = useState(0);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportError, setExportError] = useState("");
  const printRootRef = useRef(null);
  useEffect(() => {
    setOrientation("portrait");
    setPage(0);
    setExportOpen(false);
    setExportError("");
  }, [item?._id]);
  if (!item) return null;
  const pages = fiscalPreviewPages(item, orientation);
  const lastPage = Math.max(pages.length - 1, 0);
  const safePage = Math.min(Math.max(page, 0), lastPage);

  function printDocument() {
    const title = `${DOCUMENT_LABELS[item.documentType] || "Documento"} ${item.documentNumber || ""}`.trim();
    printFiscalPreviewRoot({ root: printRootRef.current, orientation, title });
  }

  async function exportDocument(format) {
    setExportError("");
    try {
      const title = `${DOCUMENT_LABELS[item.documentType] || "Documento"} ${item.documentNumber || ""}`.trim();
      const blob = await createFiscalVisualExportBlob({ root: printRootRef.current, format, orientation });
      const base = String(title || "documento-fiscale").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "documento-fiscale";
      downloadBlob(blob, `${base}.${format === "word" ? "docx" : "pdf"}`);
      setExportOpen(false);
    } catch (_error) {
      setExportError("Impossibile esportare il documento");
    }
  }

  return <>
    <UtilityFullscreenPreview
      open
      orientation={orientation}
      currentPage={safePage}
      pageCount={pages.length}
      onPageChange={setPage}
      onPrint={printDocument}
      onExport={() => { setExportError(""); setExportOpen(true); }}
      onToggleOrientation={() => { setOrientation((current) => current === "landscape" ? "portrait" : "landscape"); setPage(0); }}
      onClose={onClose}
    >
      <FiscalDocumentPreviewSheet item={item} orientation={orientation} pageIndex={safePage} pageRows={pages[safePage] || []} pageCount={pages.length} />
    </UtilityFullscreenPreview>
    {createPortal(
      <div className="documents_fiscal_preview_print_area" aria-hidden="true" ref={printRootRef}>
        {pages.map((pageRows, pageIndex) => <FiscalDocumentPreviewSheet key={`print-${pageIndex}`} item={item} orientation={orientation} pageIndex={pageIndex} pageRows={pageRows} pageCount={pages.length} printMode />)}
      </div>,
      window.document.body
    )}
    {createPortal(
      <UtilityExportOverlay open={exportOpen} title={`${DOCUMENT_LABELS[item.documentType] || "Documento"} ${item.documentNumber || ""}`.trim()} documentMode formats={["pdf", "word"]} error={exportError} onClose={() => setExportOpen(false)} onExport={exportDocument} />,
      window.document.body
    )}
  </>;
}

function prettyXml(xml = "") {
  const normalized = String(xml || "").replace(/>\s*</g, "><").replace(/(>)(<)(\/?)/g, "$1\n$2$3");
  let indent = 0;
  return normalized.split("\n").map((line) => {
    const trimmed = line.trim();
    if (/^<\//.test(trimmed)) indent = Math.max(0, indent - 1);
    const output = `${"  ".repeat(indent)}${trimmed}`;
    if (/^<[^!?/][^>]*>[^<]*$/.test(trimmed) && !/<\/[^>]+>$/.test(trimmed) && !/\/>$/.test(trimmed)) indent += 1;
    return output;
  }).join("\n");
}

function xmlNodeText(node, tagName) {
  if (!node) return "";
  return String(node.getElementsByTagName(tagName)?.[0]?.textContent || "").trim();
}

function parseFiscalXml(xml = "") {
  try {
    const documentXml = new DOMParser().parseFromString(String(xml || ""), "application/xml");
    if (documentXml.getElementsByTagName("parsererror").length) return null;
    const transmission = documentXml.getElementsByTagName("DatiTrasmissione")?.[0];
    const supplier = documentXml.getElementsByTagName("CedentePrestatore")?.[0];
    const customer = documentXml.getElementsByTagName("CessionarioCommittente")?.[0];
    const general = documentXml.getElementsByTagName("DatiGeneraliDocumento")?.[0];
    const supplierRegistry = supplier?.getElementsByTagName("Anagrafica")?.[0];
    const customerRegistry = customer?.getElementsByTagName("Anagrafica")?.[0];
    const supplierAddress = supplier?.getElementsByTagName("Sede")?.[0];
    const customerAddress = customer?.getElementsByTagName("Sede")?.[0];
    const supplierVat = supplier?.getElementsByTagName("IdFiscaleIVA")?.[0];
    const customerVat = customer?.getElementsByTagName("IdFiscaleIVA")?.[0];
    const supplierContacts = supplier?.getElementsByTagName("Contatti")?.[0];
    const customerContacts = customer?.getElementsByTagName("Contatti")?.[0];
    const paymentData = documentXml.getElementsByTagName("DatiPagamento")?.[0];
    const paymentDetails = [...documentXml.getElementsByTagName("DettaglioPagamento")];
    const paymentDetail = paymentDetails[0];
    const stampDuty = general?.getElementsByTagName("DatiBollo")?.[0];
    const generalAdjustment = general?.getElementsByTagName("ScontoMaggiorazione")?.[0];
    const lines = [...documentXml.getElementsByTagName("DettaglioLinee")].map((node) => {
      const articleCode = node.getElementsByTagName("CodiceArticolo")?.[0];
      const adjustment = node.getElementsByTagName("ScontoMaggiorazione")?.[0];
      return {
        number: xmlNodeText(node, "NumeroLinea"),
        articleCode: xmlNodeText(articleCode, "CodiceValore"),
        description: xmlNodeText(node, "Descrizione"),
        quantity: xmlNodeText(node, "Quantita"),
        unit: xmlNodeText(node, "UnitaMisura"),
        unitPrice: xmlNodeText(node, "PrezzoUnitario"),
        total: xmlNodeText(node, "PrezzoTotale"),
        vat: xmlNodeText(node, "AliquotaIVA"),
        nature: xmlNodeText(node, "Natura"),
        adjustmentType: xmlNodeText(adjustment, "Tipo"),
        adjustmentPercent: xmlNodeText(adjustment, "Percentuale"),
        adjustmentAmount: xmlNodeText(adjustment, "Importo"),
      };
    });
    const vatRows = [...documentXml.getElementsByTagName("DatiRiepilogo")].map((node) => ({
      vat: xmlNodeText(node, "AliquotaIVA"),
      nature: xmlNodeText(node, "Natura"),
      taxable: xmlNodeText(node, "ImponibileImporto"),
      tax: xmlNodeText(node, "Imposta"),
      accessoryExpenses: xmlNodeText(node, "SpeseAccessorie"),
      exigibility: xmlNodeText(node, "EsigibilitaIVA"),
      reference: xmlNodeText(node, "RiferimentoNormativo"),
    }));
    const ddt = [...documentXml.getElementsByTagName("DatiDDT")].map((node) => ({
      number: xmlNodeText(node, "NumeroDDT"),
      date: xmlNodeText(node, "DataDDT"),
      references: [...node.getElementsByTagName("RiferimentoNumeroLinea")].map((entry) => String(entry.textContent || "").trim()).filter(Boolean).join(", "),
    }));
    const orders = [...documentXml.getElementsByTagName("DatiOrdineAcquisto")].map((node) => ({
      number: xmlNodeText(node, "IdDocumento"),
      date: xmlNodeText(node, "Data"),
      references: [...node.getElementsByTagName("RiferimentoNumeroLinea")].map((entry) => String(entry.textContent || "").trim()).filter(Boolean).join(", "),
    }));
    const payments = paymentDetails.map((node) => ({
      terms: xmlNodeText(node.parentElement, "CondizioniPagamento") || xmlNodeText(paymentData, "CondizioniPagamento"),
      method: xmlNodeText(node, "ModalitaPagamento"),
      dueDate: xmlNodeText(node, "DataScadenzaPagamento"),
      amount: xmlNodeText(node, "ImportoPagamento"),
      iban: xmlNodeText(node, "IBAN"),
      bic: xmlNodeText(node, "BIC"),
      institution: xmlNodeText(node, "IstitutoFinanziario"),
    }));
    const lineNet = lines.reduce((sum, row) => sum + (Number.parseFloat(row.total) || 0), 0);
    const totalTaxable = vatRows.reduce((sum, row) => sum + (Number.parseFloat(row.taxable) || 0), 0);
    const totalTax = vatRows.reduce((sum, row) => sum + (Number.parseFloat(row.tax) || 0), 0);
    const supplierName = xmlNodeText(supplierRegistry, "Denominazione") || [xmlNodeText(supplierRegistry, "Nome"), xmlNodeText(supplierRegistry, "Cognome")].filter(Boolean).join(" ");
    const customerName = xmlNodeText(customerRegistry, "Denominazione") || [xmlNodeText(customerRegistry, "Nome"), xmlNodeText(customerRegistry, "Cognome")].filter(Boolean).join(" ");
    return {
      transmission: {
        senderCountry: xmlNodeText(transmission, "IdPaese"),
        senderCode: xmlNodeText(transmission, "IdCodice"),
        progress: xmlNodeText(transmission, "ProgressivoInvio"),
        format: xmlNodeText(transmission, "FormatoTrasmissione"),
        recipient: xmlNodeText(transmission, "CodiceDestinatario"),
        recipientPec: xmlNodeText(transmission, "PECDestinatario"),
      },
      supplier: {
        name: supplierName,
        vatCountry: xmlNodeText(supplierVat, "IdPaese"),
        vat: xmlNodeText(supplierVat, "IdCodice"),
        fiscalCode: xmlNodeText(supplier, "CodiceFiscale"),
        regime: xmlNodeText(supplier, "RegimeFiscale"),
        address: xmlNodeText(supplierAddress, "Indirizzo"),
        streetNumber: xmlNodeText(supplierAddress, "NumeroCivico"),
        cap: xmlNodeText(supplierAddress, "CAP"),
        city: xmlNodeText(supplierAddress, "Comune"),
        province: xmlNodeText(supplierAddress, "Provincia"),
        country: xmlNodeText(supplierAddress, "Nazione"),
        phone: xmlNodeText(supplierContacts, "Telefono"),
        email: xmlNodeText(supplierContacts, "Email"),
        administrationReference: xmlNodeText(supplier, "RiferimentoAmministrazione"),
      },
      customer: {
        name: customerName,
        vatCountry: xmlNodeText(customerVat, "IdPaese"),
        vat: xmlNodeText(customerVat, "IdCodice"),
        fiscalCode: xmlNodeText(customer, "CodiceFiscale"),
        address: xmlNodeText(customerAddress, "Indirizzo"),
        streetNumber: xmlNodeText(customerAddress, "NumeroCivico"),
        cap: xmlNodeText(customerAddress, "CAP"),
        city: xmlNodeText(customerAddress, "Comune"),
        province: xmlNodeText(customerAddress, "Provincia"),
        country: xmlNodeText(customerAddress, "Nazione"),
        phone: xmlNodeText(customerContacts, "Telefono"),
        email: xmlNodeText(customerContacts, "Email"),
        pec: xmlNodeText(transmission, "PECDestinatario"),
      },
      document: {
        type: xmlNodeText(general, "TipoDocumento"),
        currency: xmlNodeText(general, "Divisa"),
        date: xmlNodeText(general, "Data"),
        number: xmlNodeText(general, "Numero"),
        total: xmlNodeText(general, "ImportoTotaleDocumento"),
        reason: xmlNodeText(general, "Causale"),
        art73: xmlNodeText(general, "Art73"),
        stampDuty: xmlNodeText(stampDuty, "ImportoBollo"),
        adjustmentType: xmlNodeText(generalAdjustment, "Tipo"),
        adjustmentPercent: xmlNodeText(generalAdjustment, "Percentuale"),
        adjustmentAmount: xmlNodeText(generalAdjustment, "Importo"),
      },
      payment: payments[0] || {
        terms: xmlNodeText(paymentData, "CondizioniPagamento"),
        method: xmlNodeText(paymentDetail, "ModalitaPagamento"),
        dueDate: xmlNodeText(paymentDetail, "DataScadenzaPagamento"),
        amount: xmlNodeText(paymentDetail, "ImportoPagamento"),
        iban: xmlNodeText(paymentDetail, "IBAN"),
        bic: xmlNodeText(paymentDetail, "BIC"),
        institution: xmlNodeText(paymentDetail, "IstitutoFinanziario"),
      },
      payments,
      totals: {
        lineNet,
        taxable: totalTaxable,
        tax: totalTax,
        gross: Number.parseFloat(xmlNodeText(general, "ImportoTotaleDocumento")) || totalTaxable + totalTax,
      },
      lines,
      vatRows,
      ddt,
      orders,
    };
  } catch (_error) {
    return null;
  }
}

function fiscalXmlValue(value) {
  const text = String(value ?? "").trim();
  return text || "-";
}

function fiscalXmlEuropeanValue(value) {
  return String(value ?? "").trim();
}

function fiscalXmlEuropeanNumber(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  const number = Number.parseFloat(raw.replace(",", "."));
  if (!Number.isFinite(number)) return raw;
  return number.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: false });
}

function fiscalXmlEuropeanDate(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[3]}-${match[2]}-${match[1]}` : raw;
}

function fiscalXmlMoney(value, currency = "EUR") {
  const text = String(value ?? "").trim();
  if (!text) return "-";
  return `${text} ${currency || "EUR"}`;
}

function fiscalXmlPartyAddress(party = {}) {
  const street = [party.address, party.streetNumber].filter(Boolean).join(", ");
  const city = [party.cap, party.city, party.province ? `(${party.province})` : ""].filter(Boolean).join(" ");
  return [street, city, party.country].filter(Boolean).join(" - ") || "-";
}

function fiscalXmlAdjustmentLabel(type, percent, amount) {
  const normalized = String(type || "").toUpperCase();
  const sign = normalized === "SC" ? "-" : normalized === "MG" ? "+" : "";
  if (String(percent || "").trim()) return `${sign}${percent}%`;
  if (String(amount || "").trim()) return `${sign}${amount}`;
  return "-";
}

function fiscalXmlEuropeanAdjustment(type, percent, amount) {
  const normalized = String(type || "").trim().toUpperCase();
  const sign = normalized === "SC" ? "-" : normalized === "MG" ? "+" : "";
  if (String(percent || "").trim()) return `${sign}${fiscalXmlEuropeanNumber(percent)}`;
  if (String(amount || "").trim()) return `${sign}${fiscalXmlEuropeanNumber(amount)}`;
  return "";
}

function fiscalXmlExigibilityLabel(value = "") {
  const normalized = String(value || "").trim().toUpperCase();
  const labels = { I: "I (esigibilità immediata)", D: "D (esigibilità differita)", S: "S (scissione dei pagamenti)" };
  return labels[normalized] || normalized || "-";
}

function fiscalXmlTaxRegimeLabel(value = "") {
  const normalized = String(value || "").trim().toUpperCase();
  const labels = {
    RF01: "ordinario", RF02: "contribuenti minimi", RF04: "agricoltura e attività connesse e pesca",
    RF05: "vendita sali e tabacchi", RF06: "commercio fiammiferi", RF07: "editoria", RF08: "gestione servizi telefonia pubblica",
    RF09: "rivendita documenti di trasporto pubblico e sosta", RF10: "intrattenimenti, giochi e altre attività",
    RF11: "agenzie viaggi e turismo", RF12: "agriturismo", RF13: "vendite a domicilio", RF14: "rivendita beni usati, oggetti d’arte, antiquariato o da collezione",
    RF15: "agenzie di vendite all’asta di oggetti d’arte, antiquariato o da collezione", RF16: "IVA per cassa P.A.", RF17: "IVA per cassa",
    RF18: "altro", RF19: "regime forfettario", RF20: "agricoltura e attività connesse e pesca",
  };
  return normalized ? `${normalized}${labels[normalized] ? ` (${labels[normalized]})` : ""}` : "";
}

function fiscalXmlPaymentMethodLabel(value = "") {
  const normalized = String(value || "").trim().toUpperCase();
  const labels = {
    MP01: "MP01 contanti", MP02: "MP02 assegno", MP03: "MP03 assegno circolare", MP04: "MP04 contanti presso Tesoreria",
    MP05: "MP05 bonifico", MP06: "MP06 vaglia cambiario", MP07: "MP07 bollettino bancario", MP08: "MP08 carta di pagamento",
    MP09: "MP09 RID", MP10: "MP10 RID utenze", MP11: "MP11 RID veloce", MP12: "MP12 RIBA", MP13: "MP13 MAV",
    MP14: "MP14 quietanza erario", MP15: "MP15 giroconto su conti di contabilità speciale", MP16: "MP16 domiciliazione bancaria",
    MP17: "MP17 domiciliazione postale", MP18: "MP18 bollettino di c/c postale", MP19: "MP19 SEPA Direct Debit",
    MP20: "MP20 SEPA Direct Debit CORE", MP21: "MP21 SEPA Direct Debit B2B", MP22: "MP22 trattenuta su somme già riscosse",
    MP23: "MP23 PagoPA",
  };
  return labels[normalized] || normalized || "-";
}

function fiscalXmlDocumentTypeLabel(type = "") {
  const normalized = String(type || "").trim().toUpperCase();
  const labels = {
    TD01: "fattura",
    TD04: "nota di credito",
    TD05: "nota di debito",
    TD06: "parcella",
    TD24: "fattura differita",
    TD25: "fattura differita",
    TD26: "cessione beni ammortizzabili",
  };
  return normalized ? `${normalized}${labels[normalized] ? ` (${labels[normalized]})` : ""}` : "-";
}

function EuropeanPartyCell({ title, party, showRegime = false }) {
  const address = [party?.address, party?.streetNumber].filter(Boolean).join(" ");
  return <td className="documents_eu_party_cell">
    <strong className="documents_eu_cell_title">{title}</strong>
    <div><span>Identificativo fiscale ai fini IVA:</span> {fiscalXmlEuropeanValue(`${party?.vatCountry || ""}${party?.vat || ""}`)}</div>
    <div><span>Codice fiscale:</span> {fiscalXmlEuropeanValue(party?.fiscalCode)}</div>
    <div><span>Denominazione:</span> {fiscalXmlEuropeanValue(party?.name)}</div>
    {showRegime && <div><span>Regime fiscale:</span> {fiscalXmlTaxRegimeLabel(party?.regime)}</div>}
    <div><span>Indirizzo:</span> {fiscalXmlEuropeanValue(address)}</div>
    <div><span>Comune:</span> {fiscalXmlEuropeanValue(party?.city)} <span>Provincia:</span> {fiscalXmlEuropeanValue(party?.province)}</div>
    <div><span>Cap:</span> {fiscalXmlEuropeanValue(party?.cap)} <span>Nazione:</span> {fiscalXmlEuropeanValue(party?.country)}</div>
    {party?.phone && <div><span>Telefono:</span> {party.phone}</div>}
    {party?.email && <div><span>Email:</span> {party.email}</div>}
    {party?.pec && <div><span>Pec:</span> {party.pec}</div>}
    {showRegime && party?.administrationReference && <div><span>Riferimento Amministrazione:</span> {party.administrationReference}</div>}
  </td>;
}

function EuropeanReferenceTable({ title, rows = [] }) {
  const safeRows = rows.length ? rows : [{ number: "", date: "", references: "" }];
  return <section className="documents_eu_reference_section">
    <div className="documents_eu_section_title">{title}</div>
    <table className="documents_eu_reference_table">
      <thead><tr><th>Documento Numero</th><th>Data Documento</th><th>Righe di Riferimento</th></tr></thead>
      <tbody>{safeRows.map((row, index) => <tr key={`${title}-${row.number || index}-${index}`}><td>{fiscalXmlEuropeanValue(row.number)}</td><td>{fiscalXmlEuropeanDate(row.date)}</td><td>{fiscalXmlEuropeanValue(row.references)}</td></tr>)}</tbody>
    </table>
  </section>;
}

function EuropeanInvoiceView({ parsed }) {
  if (!parsed) return <div className="documents_xml_parse_error">Impossibile ordinare il contenuto XML.</div>;
  const currency = parsed.document.currency || "EUR";
  const grossTotal = parsed.document.total || parsed.totals.gross.toFixed(2);
  const generalAdjustment = fiscalXmlEuropeanAdjustment(parsed.document.adjustmentType, parsed.document.adjustmentPercent, parsed.document.adjustmentAmount);
  const payments = parsed.payments?.length ? parsed.payments : [parsed.payment];
  const recipientDisplay = parsed.transmission.recipientPec ? "Indicate PEC" : fiscalXmlEuropeanValue(parsed.transmission.recipient);
  return <div className="documents_xml_ordered_body documents_eu_invoice documents_eu_classic">
    <table className="documents_eu_party_table">
      <tbody><tr>
        <EuropeanPartyCell title="Cedente/prestatore (fornitore)" party={parsed.supplier} showRegime />
        <EuropeanPartyCell title="Cessionario/committente (cliente)" party={parsed.customer} />
      </tr></tbody>
    </table>

    <table className="documents_eu_document_table">
      <thead><tr><th>Tipologia documento</th><th>Art. 73</th><th>Numero documento</th><th>Data documento</th><th>Codice destinatario</th></tr></thead>
      <tbody><tr><td>{fiscalXmlDocumentTypeLabel(parsed.document.type)}</td><td>{fiscalXmlEuropeanValue(parsed.document.art73)}</td><td>{fiscalXmlEuropeanValue(parsed.document.number)}</td><td>{fiscalXmlEuropeanDate(parsed.document.date)}</td><td>{recipientDisplay}</td></tr></tbody>
    </table>

    <EuropeanReferenceTable title="Dati Relativi a DDT" rows={parsed.ddt} />
    <EuropeanReferenceTable title="Dati Relativi a Ordini ACQUISTO" rows={parsed.orders} />

    <section className="documents_eu_lines_section">
      <table className="documents_eu_lines_table">
        <thead><tr><th>Num.</th><th>Cod. articolo</th><th>Descrizione</th><th>Quantità</th><th>Prezzo unitario</th><th>UM</th><th>Sconto o magg.</th><th>%IVA</th><th>Prezzo totale</th></tr></thead>
        <tbody>
          {parsed.lines.length ? parsed.lines.map((line, index) => <tr key={`${line.number || index}-${index}`}>
            <td>{line.number || index + 1}</td>
            <td>{fiscalXmlEuropeanValue(line.articleCode)}</td>
            <td>{fiscalXmlEuropeanValue(line.description)}</td>
            <td>{fiscalXmlEuropeanNumber(line.quantity)}</td>
            <td>{fiscalXmlEuropeanNumber(line.unitPrice)}</td>
            <td>{fiscalXmlEuropeanValue(line.unit)}</td>
            <td>{fiscalXmlEuropeanAdjustment(line.adjustmentType, line.adjustmentPercent, line.adjustmentAmount)}</td>
            <td>{line.vat ? fiscalXmlEuropeanNumber(line.vat) : fiscalXmlEuropeanValue(line.nature)}</td>
            <td>{fiscalXmlEuropeanNumber(line.total)}</td>
          </tr>) : <tr><td colSpan="9"></td></tr>}
        </tbody>
      </table>
    </section>

    <section className="documents_eu_totals_section">
      <div className="documents_eu_section_title">RIEPILOGHI IVA E TOTALI</div>
      <table className="documents_eu_vat_table">
        <thead><tr><th>esigibilità iva / riferimenti normativi</th><th>%IVA</th><th>Spese accessorie</th><th>Totale imponibile</th><th>Totale imposta</th></tr></thead>
        <tbody>{parsed.vatRows.length ? parsed.vatRows.map((row, index) => <tr key={`${row.vat || row.nature}-${index}`}><td>{fiscalXmlEuropeanValue(row.reference || (row.exigibility ? fiscalXmlExigibilityLabel(row.exigibility) : row.nature))}</td><td>{row.vat ? fiscalXmlEuropeanNumber(row.vat) : fiscalXmlEuropeanValue(row.nature)}</td><td>{fiscalXmlEuropeanNumber(row.accessoryExpenses)}</td><td>{fiscalXmlEuropeanNumber(row.taxable)}</td><td>{fiscalXmlEuropeanNumber(row.tax)}</td></tr>) : <tr><td colSpan="5"></td></tr>}</tbody>
      </table>
      <table className="documents_eu_grand_totals_table">
        <tbody>
          <tr><th>Importo bollo</th><th>Sconto/Maggiorazione</th><th>Valuta</th><th>Totale documento</th></tr>
          <tr><td>{fiscalXmlEuropeanNumber(parsed.document.stampDuty)}</td><td>{generalAdjustment}</td><td>{currency}</td><td>{fiscalXmlEuropeanNumber(grossTotal)}</td></tr>
        </tbody>
      </table>
    </section>

    <section className="documents_eu_payments_section">
      <table className="documents_eu_payments_table">
        <thead><tr><th>Modalità pagamento</th><th>IBAN</th><th>Istituto</th><th>Data scadenza</th><th>Importo</th></tr></thead>
        <tbody>{payments.map((payment, index) => <tr key={`payment-${index}`}><td>{payment?.method ? fiscalXmlPaymentMethodLabel(payment.method || payment.terms) : ""}</td><td>{fiscalXmlEuropeanValue(payment?.iban)}</td><td>{fiscalXmlEuropeanValue(payment?.institution)}</td><td>{fiscalXmlEuropeanDate(payment?.dueDate)}</td><td>{fiscalXmlEuropeanNumber(payment?.amount)}</td></tr>)}</tbody>
      </table>
    </section>
  </div>;
}


function FiscalXmlFullscreenPreview({ item, xml, onClose }) {
  if (!item || !xml) return null;
  const parsed = parseFiscalXml(xml);

  function printPanel(target) {
    const className = target === "raw" ? "documents_xml_print_raw" : "documents_xml_print_ordered";
    document.body.classList.add(className);
    const cleanup = () => document.body.classList.remove(className);
    window.addEventListener("afterprint", cleanup, { once: true });
    printUtilityPreview("portrait");
    window.setTimeout(cleanup, 1600);
  }

  return createPortal(
    <div className="documents_xml_fullscreen" role="dialog" aria-modal="true" aria-label={`XML fattura ${item.documentNumber || ""}`}>
      <div className="documents_xml_toolbar"><div><span>Fattura XML</span><strong>{item.documentNumber || "-"}</strong></div><button type="button" className="utility_icon_action_button utility_fullscreen_exit" onClick={onClose} aria-label="Chiudi XML" title="Chiudi XML">×</button></div>
      <div className="documents_xml_split">
        <section className="documents_xml_panel documents_xml_raw_panel">
          <header><div><span>XML completo</span><strong>Tag e valori del documento</strong></div><UtilityActionIconButton action="print" className="documents_xml_print_button" onClick={() => printPanel("raw")} title="Stampa XML" /></header>
          <pre>{prettyXml(xml)}</pre>
        </section>
        <section className="documents_xml_panel documents_xml_ordered_panel">
          <header><div><span>Formato documento</span><strong>Formato Europeo Ordinato</strong></div><UtilityActionIconButton action="print" className="documents_xml_print_button" onClick={() => printPanel("ordered")} title="Stampa formato europeo" /></header>
          <EuropeanInvoiceView parsed={parsed} />
        </section>
      </div>
    </div>,
    window.document.body
  );
}

function FiscalActionButton({ label, onClick, disabled = false, reason = "", primary = false }) {
  const hasTooltip = disabled && Boolean(reason);
  return <span className={`documents_fiscal_action_control${disabled ? " is_disabled" : ""}${hasTooltip ? " has_tooltip" : ""}`} data-tooltip={hasTooltip ? reason : undefined} tabIndex={hasTooltip ? 0 : undefined} aria-label={hasTooltip ? reason : undefined}>
    <button type="button" className={`management_action_button${primary ? " management_action_primary" : ""}`} disabled={disabled} onClick={onClick}>{label}</button>
  </span>;
}

function FiscalActions({ item, canSend = true, onCancelled }) {
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState("");
  const active = item.status === "ISSUED";
  async function share(channel) {
    if (!active || !canSend) return;
    setBusy(channel); setMessage("");
    try {
      if (channel === "email") {
        if (!item.customerEmail) return setMessage("Email cliente mancante");
        await sendFiscalDocumentEmail(item._id, item.customerEmail);
        setMessage("Email inviata");
      } else {
        const data = await createFiscalShareLink(item._id);
        const body = `Documento ${item.documentNumber}: ${data.url}`;
        const phone = normalizePhone(item.customerPhone);
        if (!phone) return setMessage("Telefono cliente mancante");
        if (channel === "whatsapp") window.open(`https://wa.me/${phone}?text=${encodeURIComponent(body)}`, "_blank", "noopener,noreferrer");
        else window.location.href = `sms:${phone}?body=${encodeURIComponent(body)}`;
      }
    } catch (err) { setMessage(responseError(err, "Errore condivisione documento")); } finally { setBusy(""); }
  }
  async function printDocument() {
    setBusy("print"); setMessage("");
    try { const data = await createFiscalShareLink(item._id); window.open(data.url, "_blank", "noopener,noreferrer"); } catch (err) { setMessage(responseError(err, "Errore apertura documento")); } finally { setBusy(""); }
  }
  async function xml() {
    setBusy("xml"); setMessage("");
    try { const blob = await getFiscalDocumentXml(item._id); downloadBlob(blob, `${String(item.documentNumber || "fattura").replace(/[^a-z0-9.-]+/gi, "_")}.xml`); } catch (err) { setMessage(responseError(err, "Errore generazione XML")); } finally { setBusy(""); }
  }
  return <div className="documents_action_stack"><div className="documents_card_actions">{active && <button type="button" className="management_action_button management_action_primary" disabled={!!busy} onClick={printDocument}>Visualizza / Stampa</button>}{active && item.documentType === "INVOICE" && <button type="button" className="management_action_button" disabled={!!busy} onClick={xml}>XML</button>}{active && canSend && <button type="button" className="management_action_button" disabled={!!busy} onClick={() => share("email")}>Mail</button>}{active && canSend && <button type="button" className="management_action_button" disabled={!!busy} onClick={() => share("whatsapp")}>WhatsApp</button>}{active && canSend && <button type="button" className="management_action_button" disabled={!!busy} onClick={() => share("sms")}>Messaggio</button>}{onCancelled && active && <button type="button" className="management_action_button management_action_danger documents_cancel_button" disabled={!!busy} onClick={onCancelled}>Annulla Documento</button>}</div>{message && <small className="documents_action_message">{message}</small>}</div>;
}

function InvoicingFiscalActions({ item, customer, canSend = true, onOpenPrint, onOpenXml }) {
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState("");
  const mobile = registryMobile(customer);
  const email = registryEmail(customer);
  const whatsappReason = !canSend ? "Operazione non autorizzata" : !mobile ? "Numero Cellulare Mancante" : "";
  const mailReason = !canSend ? "Operazione non autorizzata" : !email ? "Mail Mancante" : "";
  const xmlReason = item?.documentType === "INVOICE" ? "" : "XML disponibile solo per le fatture";

  async function share(channel) {
    if (busy || !canSend) return;
    setBusy(channel);
    setMessage("");
    try {
      if (channel === "email") {
        if (!email) return;
        await sendFiscalDocumentEmail(item._id, email);
        setMessage("Email inviata");
        return;
      }
      if (!mobile) return;
      const data = await createFiscalShareLink(item._id);
      const body = `Documento ${item.documentNumber}: ${data.url}`;
      window.open(`https://wa.me/${mobile}?text=${encodeURIComponent(body)}`, "_blank", "noopener,noreferrer");
    } catch (err) {
      setMessage(responseError(err, "Errore condivisione documento"));
    } finally {
      setBusy("");
    }
  }

  async function openXml() {
    if (busy || item?.documentType !== "INVOICE") return;
    setBusy("xml");
    setMessage("");
    try {
      const blob = await getFiscalDocumentXml(item._id);
      const xml = await blob.text();
      onOpenXml?.(item, xml);
    } catch (err) {
      setMessage(responseError(err, "Errore apertura XML"));
    } finally {
      setBusy("");
    }
  }

  return <div className="documents_action_stack documents_invoicing_action_stack">
    <div className="documents_invoicing_actions">
      <FiscalActionButton label="Stampa" primary disabled={!!busy} onClick={() => onOpenPrint?.(item)} />
      <FiscalActionButton label="XML" primary disabled={!!busy || !!xmlReason} reason={xmlReason} onClick={openXml} />
      <FiscalActionButton label="Whatsapp" primary disabled={!!busy || !!whatsappReason} reason={whatsappReason} onClick={() => share("whatsapp")} />
      <FiscalActionButton label="Mail" primary disabled={!!busy || !!mailReason} reason={mailReason} onClick={() => share("email")} />
    </div>
    {message && <small className="documents_action_message">{message}</small>}
  </div>;
}

export function FreeInvoices() {
  const { customers, sites } = useReferenceData();
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const level = getUserPageLevel(readStoredUser(), "freeInvoice");
  const list = useManagementList(items, FREE_INVOICE_FILTER_DEFINITIONS, (item) => [item.documentNumber, item.customerName, item.customerEmail, item.siteDescription, item.note].join(" "));

  async function reload() {
    setLoading(true);
    try { const data = await getFreeInvoices(); setItems(data?.items || []); setError(""); } catch (err) { setError(responseError(err, "Errore caricamento fatture libere")); } finally { setLoading(false); }
  }
  useEffect(() => { reload(); }, []);
  async function save() {
    if (!String(draft?.customerName || "").trim()) return setError("Inserisci l'intestatario della fattura");
    if (!(draft?.rows || []).some((row) => String(row.description || "").trim())) return setError("Inserisci almeno una riga fattura");
    try { await createFreeInvoice(draft); setDraft(null); setError(""); await reload(); } catch (err) { setError(responseError(err, "Errore emissione fattura libera")); }
  }
  async function cancel(item) {
    if (!window.confirm(`Annullare il documento ${item.documentNumber}? Rimarrà nello storico.`)) return;
    try { await cancelFreeInvoice(item._id); await reload(); } catch (err) { setError(responseError(err, "Errore annullamento documento")); }
  }
  function newInvoice() { setDraft(emptyInvoice()); setError(""); }

  return <section className="window_page bookings_page management_page management_shared_page documents_management_page">
    <DocumentBanner title="FATTURE" subtitle="Emetti fatture libere non collegate a una prenotazione usando lo stesso registro fiscale dei soggiorni." />
    <div className="management_list_panel documents_management_panel">
      <DocumentToolbar actions={[{ label: "Nuova Fattura", onClick: newInvoice }, { label: "Azzera ricerca e filtri", onClick: list.reset }, { label: "Aggiorna", onClick: reload }]} search={list.search} onSearchChange={list.setSearch} results={list.filtered.length} />
      {error && !draft && <div className="management_table_visible_status is_error">{error}</div>}
      <div className="management_board management_table_board"><div className="management_table_shell" role="table" aria-label="Elenco fatture libere" ref={list.shellRef} onScroll={list.handleScroll}>
        <div className="management_table_header management_shared_table_header documents_free_invoices_table_header" role="row">
          {FREE_INVOICE_FILTER_DEFINITIONS.map((definition) => <FilterCell key={definition.key} definition={definition} list={list} />)}
          <div className="management_table_actions_head" role="columnheader">Azioni</div>
        </div>
        <TableState loading={loading} empty={!loading && list.filtered.length === 0} loadingText="Caricamento Fatture" emptyText="Nessun Risultato Trovato" />
        <div className="management_table_body">{list.rows.map((item) => <article className="management_table_row documents_free_invoices_table_row" key={item._id} data-vesta-focus-id={item._id} role="row">
          <div className="management_table_cell management_table_title_cell" role="cell"><strong>{item.documentNumber || "-"}</strong></div>
          <div className="management_table_cell" role="cell"><div className="documents_customer_cell"><strong>{item.customerName || "-"}</strong><span>{item.customerEmail || ""}</span></div></div>
          <div className="management_table_cell" role="cell">{dateLabel(item.issuedAt)}</div>
          <div className="management_table_cell" role="cell">{item.siteDescription || "Sede non indicata"}</div>
          <div className="management_table_cell" role="cell"><StatusBadge status={item.status} /></div>
          <div className="management_table_cell documents_money_cell" role="cell"><strong>{money(item.grossTotal)}</strong></div>
          <div className="management_table_actions management_status_actions documents_fiscal_actions_cell" role="cell"><FiscalActions item={item} canSend={level >= 1} onCancelled={level >= 3 ? () => cancel(item) : null} /></div>
        </article>)}</div>
      </div></div>
    </div>
    {draft && <EditorOverlay kind="invoice" draft={draft} customers={customers} sites={sites} error={error} onChange={(patch) => setDraft((current) => ({ ...current, ...patch }))} onClose={() => setDraft(null)} onSave={save} />}
  </section>;
}

export function Invoicing() {
  const { customers, sites } = useReferenceData();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [periodFilters, setPeriodFilters] = useState({ from: "", to: "", siteId: "" });
  const [previewItem, setPreviewItem] = useState(null);
  const [xmlPreview, setXmlPreview] = useState(null);
  const level = getUserPageLevel(readStoredUser(), "invoicing");

  async function reload() {
    setLoading(true);
    try {
      const data = await getFiscalDocuments({ documentType: "INVOICE", status: "ISSUED", from: periodFilters.from || undefined, to: periodFilters.to || undefined });
      setItems((data?.items || []).filter((item) => item.documentType === "INVOICE")); setError("");
    } catch (err) { setError(responseError(err, "Errore caricamento documenti fiscali")); } finally { setLoading(false); }
  }
  useEffect(() => { reload(); }, [periodFilters.from, periodFilters.to]);

  const periodItems = useMemo(() => items.filter((item) => !periodFilters.siteId || String(item.siteId || "") === String(periodFilters.siteId)), [items, periodFilters.siteId]);
  const list = useManagementList(periodItems, INVOICING_FILTER_DEFINITIONS, (item) => [item.documentNumber, item.customerName, item.customerEmail, item.siteDescription, item.roomNumber].join(" "));
  const bookingInvoices = useMemo(() => periodItems.filter((item) => item.documentType === "INVOICE" && item.purpose === "CHECKOUT" && item.booking_id), [periodItems]);

  async function downloadArchive() {
    setArchiveBusy(true); setError("");
    try {
      const data = await getFiscalXmlArchive({ from: periodFilters.from || undefined, to: periodFilters.to || undefined, siteId: periodFilters.siteId || undefined });
      if (!data?.entries?.length) { setError("Nessuna fattura da esportare nel periodo selezionato"); return; }
      const blob = buildStoredZip(data.entries.map((entry) => ({ name: `${entry.path}/${entry.filename}`, data: entry.xml })));
      downloadBlob(blob, `${data.root || "Azienda"}_XML_Fatture_${todayToken()}.zip`);
    } catch (err) { setError(responseError(err, "Errore generazione archivio XML")); } finally { setArchiveBusy(false); }
  }

  function resetAllFilters() {
    setPeriodFilters({ from: "", to: "", siteId: "" });
    list.reset();
  }

  return <section className="window_page bookings_page management_page management_shared_page documents_management_page">
    <DocumentBanner title="FATTURAZIONE" subtitle="Gestisci le fatture emesse e genera gli XML delle fatture concluse per Azienda / Sede / Anno / Mese." />
    <div className="management_list_panel documents_management_panel">
      <DocumentToolbar
        actions={[{ label: archiveBusy ? "Generazione XML..." : `Scarica Archivio XML (${bookingInvoices.length})`, hidden: level < 1, disabled: archiveBusy, onClick: downloadArchive }, { label: "Azzera ricerca e filtri", onClick: resetAllFilters }, { label: "Aggiorna", onClick: reload }]}
        search={list.search}
        onSearchChange={list.setSearch}
        results={list.filtered.length}
        filtersExpanded={filtersExpanded}
        onToggleFilters={() => setFiltersExpanded((current) => !current)}
      >
        <div className="documents_period_filters" aria-label="Filtri periodo fatturazione" aria-hidden={!filtersExpanded}>
          <label><span>Dal</span><input type="date" value={periodFilters.from} onChange={(event) => setPeriodFilters((current) => ({ ...current, from: event.target.value }))} /></label>
          <label><span>Al</span><input type="date" value={periodFilters.to} onChange={(event) => setPeriodFilters((current) => ({ ...current, to: event.target.value }))} /></label>
          <label><span>Sede</span><VestaSelect value={periodFilters.siteId} onChange={(event) => setPeriodFilters((current) => ({ ...current, siteId: event.target.value }))}><option value="">Tutte le sedi</option>{sites.map((site) => <option key={site._id} value={site._id}>{site.description}</option>)}</VestaSelect></label>
        </div>
      </DocumentToolbar>
      {error && <div className="management_table_visible_status is_error">{error}</div>}
      <div className="management_board management_table_board"><div className="management_table_shell" role="table" aria-label="Elenco fatturazione elettronica" ref={list.shellRef} onScroll={list.handleScroll}>
        <div className="management_table_header management_shared_table_header documents_invoicing_table_header" role="row">
          {INVOICING_FILTER_DEFINITIONS.map((definition) => <FilterCell key={definition.key} definition={definition} list={list} />)}
          <div className="management_table_actions_head documents_invoicing_actions_head" role="columnheader">Azioni</div>
        </div>
        <TableState loading={loading} empty={!loading && list.filtered.length === 0} loadingText="Caricamento Fatturazione" emptyText="Nessun Risultato Trovato" />
        <div className="management_table_body">{list.rows.map((item) => <article className="management_table_row documents_invoicing_table_row" key={item._id} data-vesta-focus-id={item._id} role="row">
          <div className="management_table_cell management_table_title_cell" role="cell"><strong>{item.documentNumber || "-"}</strong></div>
          <div className="management_table_cell" role="cell"><div className="documents_customer_cell"><strong>{item.customerName || "-"}</strong><span>{item.customerEmail || ""}</span></div></div>
          <div className="management_table_cell" role="cell">{dateLabel(item.issuedAt)}</div>
          <div className="management_table_cell" role="cell">{item.siteDescription || "Sede non indicata"}</div>
          <div className="management_table_cell" role="cell">{item.roomNumber || "-"}</div>
          <div className="management_table_cell documents_money_cell" role="cell"><strong>{money(item.grossTotal)}</strong></div>
          <div className="management_table_actions management_status_actions documents_fiscal_actions_cell" role="cell"><InvoicingFiscalActions item={item} customer={fiscalRegistryCustomer(item, customers)} canSend={level >= 1} onOpenPrint={setPreviewItem} onOpenXml={(documentItem, xml) => setXmlPreview({ item: documentItem, xml })} /></div>
        </article>)}</div>
      </div></div>
    </div>
    <FiscalDocumentFullscreenPreview item={previewItem} onClose={() => setPreviewItem(null)} />
    <FiscalXmlFullscreenPreview item={xmlPreview?.item || null} xml={xmlPreview?.xml || ""} onClose={() => setXmlPreview(null)} />
  </section>;
}
